import { useMemo } from 'react';
import { normalizeId } from '../utils/idNormalization';

/**
 * Adds collapse state to codes, handling both flat and hierarchical structures
 * @param {Array} codes - Array of codes (flat or hierarchical)
 * @param {Object} collapseState - Map of code IDs to collapse state
 * @returns {Array} Codes with collapsed property added
 */
function addCollapseStateToCodes(codes, collapseState = {}) {
  return codes.map(code => {
    const normalizedId = normalizeId(code.id);
    const collapsed = collapseState[normalizedId] || false;
    
    const codeWithCollapse = {
      ...code,
      collapsed
    };
    
    // If this code has children, recursively add collapse state to them
    if (code.children && code.children.length > 0) {
      codeWithCollapse.children = addCollapseStateToCodes(code.children, collapseState);
    }
    
    return codeWithCollapse;
  });
}

/**
 * Generic hook that enhances any codes data with collapse state
 * Works with both flat arrays (useCodesSidebar) and hierarchical arrays (useCodesHierarchy)
 * @param {Object} codesData - Object containing codes array and loading/error states
 * @param {Object} collapseData - Object containing collapse state data and loading/error states
 * @returns {Object} Enhanced codes with collapse state
 */
export function useCodesWithCollapse(codesData, collapseData) {
  const { 
    codes: originalCodes, 
    isLoading: codesLoading,
    error: codesError
  } = codesData;
  
  const {
    data: collapseState,
    isLoading: collapseLoading,
    error: collapseError
  } = collapseData;
  
  // Enhance codes with collapse state
  const codesWithCollapse = useMemo(() => {
    if (!originalCodes || originalCodes.length === 0) {
      return [];
    }
    
    return addCollapseStateToCodes(originalCodes, collapseState || {});
  }, [originalCodes, collapseState]);
  
  return {
    codes: codesWithCollapse,
    isLoading: codesLoading || collapseLoading,
    error: codesError || collapseError
  };
}